import React from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { MdCheckCircle, MdError, MdLink, MdDragIndicator } from 'react-icons/md';
import { FaTrashAlt } from "react-icons/fa";
import { Grid, Chip, IconButton } from '@material-ui/core';
import { Star } from '@material-ui/icons';

// DND-KIT Imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import 'react-circular-progressbar/dist/styles.css';
import { Container, FileInfo, Preview } from './styles';

const MIN_WIDTH = 800;
const MIN_HEIGHT = 1000;
const RECOMMENDED_RATIO = 4 / 5;
const RATIO_TOLERANCE = 0.08;

// --- SUB-COMPONENTE PARA CADA ITEM DA LISTA ---
const SortableItem = ({ uploadedFile, index, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: uploadedFile.idTemp || String(uploadedFile.id) });

    const isPrincipal = index === 0;
    const hasDimensions = typeof uploadedFile.width === 'number' && typeof uploadedFile.height === 'number';
    const isLowResolution = hasDimensions && (uploadedFile.width < MIN_WIDTH || uploadedFile.height < MIN_HEIGHT);
    const isOutOfRatio = hasDimensions
        && typeof uploadedFile.ratio === 'number'
        && Math.abs(uploadedFile.ratio - RECOMMENDED_RATIO) > RATIO_TOLERANCE;

    const borderColor = isLowResolution ? '#e65100' : isOutOfRatio ? '#fb8c00' : '#d5d5d5';
    const borderWidth = isPrincipal ? 2 : 1;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 999 : 1,
        position: 'relative',
        background: '#fff',
        listStyle: 'none',
        marginBottom: '10px',
        border: `${borderWidth}px solid ${borderColor}`,
        borderRadius: '4px',
        boxShadow: isLowResolution
            ? '0 0 0 2px rgba(230, 81, 0, 0.12)'
            : isOutOfRatio
                ? '0 0 0 2px rgba(251, 140, 0, 0.12)'
                : 'none',
    };

    return (
        <li ref={setNodeRef} style={style}>
            <FileInfo>
                {/* Handle de Arrasto */}
                <div
                    {...attributes}
                    {...listeners}
                    style={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 10px',
                        touchAction: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none'
                    }}
                >
                    <MdDragIndicator size={24} color="#999" />
                </div>

                <Preview src={uploadedFile.preview} />

                <Grid container alignItems="center">
                    <Grid item xs={12} sm={6}>
                        {isPrincipal ? (
                            <Chip
                                icon={<Star style={{ color: '#ffd700' }} />}
                                label="FOTO PRINCIPAL / CAPA"
                                style={{ backgroundColor: '#356C7C', color: '#fff', fontWeight: 'bold' }}
                                size="small"
                            />
                        ) : (
                            <span style={{ fontSize: '12px', color: '#999' }}>Arraste para o topo para definir como imagem principal/capa.</span>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6} style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', flexWrap: 'wrap' }}>
                        {hasDimensions && (
                            <Chip
                                label={`${uploadedFile.width}x${uploadedFile.height}`}
                                size="small"
                                variant="outlined"
                                style={{ fontWeight: 600 }}
                            />
                        )}
                        {isLowResolution && (
                            <Chip
                                label="Resolução baixa"
                                size="small"
                                style={{ backgroundColor: '#fbe9e7', color: '#bf360c', fontWeight: 600 }}
                            />
                        )}
                        {isOutOfRatio && (
                            <Chip
                                label="Fora do padrão 4:5"
                                size="small"
                                style={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
                            />
                        )}
                    </Grid>

                </Grid>
            </FileInfo>

            <div style={{ display: 'flex', alignItems: 'center', paddingRight: '10px' }}>
                {!uploadedFile.uploaded && !uploadedFile.error && (
                    <CircularProgressbar
                        styles={{
                            root: { width: 24 },
                            path: { stroke: '#356C7C' }
                        }}
                        strokeWidth={10}
                        value={uploadedFile.progress}
                    />
                )}
                {uploadedFile.uploaded && <MdCheckCircle size={24} color='#78e5d5' />}
                {uploadedFile.url && (
                    <a href={uploadedFile.url} target='_blank' rel='noopener noreferrer'>
                        <MdLink style={{ margin: 5 }} size={24} color='#222' />
                    </a>
                )}
                {(uploadedFile.uploaded || uploadedFile.error) && onDelete && <IconButton
                    size="small"
                    color='primary'
                    variant='outlined'
                    onClick={() => onDelete(uploadedFile.id, uploadedFile)}
                ><FaTrashAlt size={20} color='#e57878' /></IconButton>}
                {uploadedFile.error && <MdError size={24} color='#e57878' />}
            </div>
        </li>
    );
};

// --- COMPONENTE PRINCIPAL ---
const FileList = ({ files, onDelete, onReorder }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), // evita cliques acidentais dispararem arrasto
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 120,
                tolerance: 6,
            },
        }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const itemIds = React.useMemo(() => files.map(f => f.idTemp || String(f.id)), [files]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // A busca do index deve usar a mesma lógica de ID
        const oldIndex = files.findIndex(f => (f.idTemp || String(f.id)) === active.id);
        const newIndex = files.findIndex(f => (f.idTemp || String(f.id)) === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newFiles = arrayMove(files, oldIndex, newIndex);
            onReorder(newFiles);
        }
    };

    return (
        <Container>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                    {files.map((uploadedFile, index) => (
                        <SortableItem
                            // Use o mesmo ID que está no array itemIds
                            key={uploadedFile.idTemp || String(uploadedFile.id)}
                            uploadedFile={uploadedFile}
                            index={index}
                            onDelete={onDelete}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </Container>
    );
}

export default FileList;